import type { CatalogFacts } from './catalog.ts';
import { lineGrams } from './grams.ts';
import { NUTRIENT_KEYS, type Nutrients } from './nutrients.ts';
import type { Amount } from './quantity.ts';

/** One ingredient line as the calculator sees it. */
export interface CalcLine extends Amount {
  id: string;
  name: string;
  optional: boolean;
  /** Stored weight (from the import or a fix); computed from the catalog when null. */
  grams: number | null;
  /** The matched catalog ingredient with the household's effective prices, or null. */
  ingredient: CatalogFacts | null;
}

export type MissingReason = 'unmatched' | 'no_weight' | 'no_nutrients' | 'no_price';

export interface MissingLine {
  lineId: string;
  name: string;
  reason: MissingReason;
}

export interface RecipeFigures {
  /** Whole recipe as written (the yield). */
  total: Nutrients;
  perPortion: Nutrients;
  /** "Computed from 11 of 12 ingredients". */
  coverage: { counted: number; total: number; missing: MissingLine[] };
  /** Shown with ≈ when true (DESIGN.md §10 EstimateMarker). */
  estimate: boolean;
  /** Why it's an estimate: lines from unverified catalog entries or with guessed weights. */
  estimateLines: { unverified: string[]; guessedWeight: string[] };
  cost: {
    total: number | null;
    perPortion: number | null;
    priced: number;
    missing: MissingLine[];
  };
}

export const zeroNutrients = (): Nutrients =>
  Object.fromEntries(NUTRIENT_KEYS.map((k) => [k, 0])) as Nutrients;

export function scaleNutrients(n: Nutrients, factor: number): Nutrients {
  return Object.fromEntries(NUTRIENT_KEYS.map((k) => [k, n[k] * factor])) as Nutrients;
}

/** The weight of a line and whether it was guessed. */
function weigh(line: CalcLine): { grams: number; guessed: boolean } | null {
  if (line.grams !== null) {
    return { grams: line.grams, guessed: line.unit === null };
  }
  if (!line.ingredient) return null;
  const weighed = lineGrams(line, line.ingredient);
  return weighed ? { grams: weighed.grams, guessed: weighed.basis !== 'measured' } : null;
}

/** Price of a line: by weight, else by the piece for counted items. */
function price(line: CalcLine, grams: number | null): number | null {
  const facts = line.ingredient;
  if (!facts) return null;
  if (grams !== null && facts.priceEurPerKg !== null) return (grams / 1000) * facts.priceEurPerKg;
  if (
    facts.priceEurPerPiece !== null &&
    line.quantity !== null &&
    (line.unit === null || line.unit === 'piece')
  ) {
    const q = line.quantityMax != null ? (line.quantity + line.quantityMax) / 2 : line.quantity;
    return q * facts.priceEurPerPiece;
  }
  return null;
}

/**
 * Nutrition and price of a recipe (PLAN.md §6). A line counts toward nutrition when it is
 * matched, weighable and its ingredient has nutrients; optional lines and lines without a
 * quantity ("Salz nach Geschmack") are left out of both the sums and the coverage count.
 * Anything from an unverified catalog entry or a guessed unit weight marks the result as an
 * estimate; cost is always an estimate.
 */
export function recipeFigures(lines: CalcLine[], yieldPortions: number): RecipeFigures {
  const total = zeroNutrients();
  const missing: MissingLine[] = [];
  const costMissing: MissingLine[] = [];
  const unverified = new Set<string>();
  const guessed = new Set<string>();
  let counted = 0;
  let considered = 0;
  let cost = 0;
  let priced = 0;

  for (const line of lines) {
    if (line.optional || line.quantity === null) continue;
    considered++;
    const miss = (reason: MissingReason) => {
      missing.push({ lineId: line.id, name: line.name, reason });
    };
    const weighed = weigh(line);
    const linePrice = price(line, weighed?.grams ?? null);
    if (linePrice === null) {
      costMissing.push({
        lineId: line.id,
        name: line.name,
        reason: line.ingredient ? 'no_price' : 'unmatched',
      });
    } else {
      cost += linePrice;
      priced++;
    }

    if (!line.ingredient) {
      miss('unmatched');
      continue;
    }
    if (!weighed) {
      miss('no_weight');
      continue;
    }
    const nutrients = line.ingredient.nutrients;
    if (!nutrients) {
      miss('no_nutrients');
      continue;
    }
    counted++;
    for (const key of NUTRIENT_KEYS) total[key] += (weighed.grams * nutrients[key]) / 100;
    if (!line.ingredient.verified) unverified.add(line.name);
    if (weighed.guessed) guessed.add(line.name);
  }

  const portions = yieldPortions > 0 ? yieldPortions : 1;
  const hasCost = priced > 0;
  return {
    total,
    perPortion: scaleNutrients(total, 1 / portions),
    coverage: { counted, total: considered, missing },
    estimate: counted < considered || unverified.size > 0 || guessed.size > 0,
    estimateLines: { unverified: [...unverified], guessedWeight: [...guessed] },
    cost: {
      total: hasCost ? cost : null,
      perPortion: hasCost ? cost / portions : null,
      priced,
      missing: costMissing,
    },
  };
}

/** "Your portion" (CONTEXT.md): a portion × the user's portion multiplier. */
export const yourPortion = (perPortion: Nutrients, multiplier: number) =>
  scaleNutrients(perPortion, multiplier);

/** Cost of cooking `portions` of a recipe (a cook session). */
export function sessionCost(figures: RecipeFigures, portions: number): number | null {
  return figures.cost.perPortion === null ? null : figures.cost.perPortion * portions;
}
